import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Wallet, AlertTriangle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ParcelasPlanoTable } from './ParcelasPlanoTable';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const money = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const br = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const errMsg = (e, d) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : d);

const fromItem = (p, cy) => ({
  label: p.label || '', amount: p.valor ?? '', installments: p.parcelas || 1,
  first_month: p.mes_inicial || 1, first_year: p.ano_inicial || cy, due_day: p.dia_vencimento || 10,
  discount_type: p.desconto_tipo || 'none', discount_value: p.desconto_valor || 0,
  abatimento: p.abatimento || 0, notes: p.observacao || '',
});

const Field = ({ label, children, className = '' }) => (
  <div className={className}><Label className="text-xs font-semibold text-slate-700">{label}</Label>{children}</div>
);
const Kpi = ({ label, value, tone = 'text-slate-900', testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`text-sm font-bold ${tone}`}>{value}</p>
  </div>
);
const Sel = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">{children}</select>
);

function useContext(titleId, open) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!open || !titleId) return;
    setState((s) => ({ ...s, loading: !s.data, error: null }));
    api.get(`/student-contracts/title/${titleId}/plan-context`)
      .then(({ data }) => setState({ loading: false, data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: errMsg(e, 'Falha ao carregar o plano do aluno') }));
  }, [titleId, open, tick]);
  useEffect(() => { if (!open) setState({ loading: false, data: null, error: null }); }, [open]);
  return { ...state, reload: () => setTick((x) => x + 1) };
}

// Corpo enviado: mês/ano/dia só quando mudaram (evita recalcular datas sem necessidade).
const bodyOf = (f, orig) => {
  const diff = (k) => Number(f[k]) !== Number(orig[k]);
  return {
    label: f.label, amount: num(f.amount), installments: num(f.installments),
    first_month: diff('first_month') ? num(f.first_month) : null,
    first_year: diff('first_year') ? num(f.first_year) : null,
    due_day: diff('due_day') ? num(f.due_day) : null,
    discount_type: f.discount_type, discount_value: num(f.discount_value) || 0,
    abatimento: num(f.abatimento) || 0, notes: f.notes, overwrite_custom: !!f.overwrite_custom,
  };
};

function PlanForm({ f, setF, anos }) {
  const s = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Field label="Serviço / plano" className="col-span-2 sm:col-span-4"><Input value={f.label} onChange={s('label')} data-testid="pm-label" /></Field>
      <Field label="Valor da parcela (R$)"><Input inputMode="decimal" value={f.amount} onChange={s('amount')} data-testid="pm-valor" /></Field>
      <Field label="Nº de parcelas"><Input type="number" min={1} max={60} value={f.installments} onChange={s('installments')} data-testid="pm-parcelas" /></Field>
      <Field label="Mês inicial"><Sel value={f.first_month} onChange={s('first_month')} testid="pm-mes">{MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</Sel></Field>
      <Field label="Ano inicial"><Sel value={f.first_year} onChange={s('first_year')} testid="pm-ano">{anos.map((a) => <option key={a} value={a}>{a}</option>)}</Sel></Field>
      <Field label="Dia de vencimento"><Input type="number" min={1} max={31} value={f.due_day} onChange={s('due_day')} data-testid="pm-dia" /></Field>
      <Field label="Desconto · tipo"><Sel value={f.discount_type} onChange={s('discount_type')} testid="pm-desc-tipo">
        <option value="none">Sem desconto</option><option value="percent">Percentual (%)</option><option value="fixed">Valor fixo (R$)</option></Sel></Field>
      <Field label={f.discount_type === 'percent' ? 'Desconto (%)' : 'Desconto (R$)'}><Input inputMode="decimal" value={f.discount_value} disabled={f.discount_type === 'none'} onChange={s('discount_value')} data-testid="pm-desc-valor" /></Field>
      <Field label="Abatimento por parcela (R$)"><Input inputMode="decimal" value={f.abatimento} onChange={s('abatimento')} data-testid="pm-abatimento" /></Field>
      <Field label="Observação" className="col-span-2 sm:col-span-4"><Input value={f.notes} onChange={s('notes')} data-testid="pm-obs" /></Field>
    </div>
  );
}

function Preview({ pv, loading }) {
  if (!pv) return <p className="text-sm text-slate-500">{loading ? 'Calculando prévia…' : 'Prévia indisponível.'}</p>;
  return (
    <div className="space-y-2" data-testid="pm-previa">
      <div className="rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-sm text-violet-900">
        <p className="font-bold" data-testid="pm-previa-datas">
          {pv.parcelas} parcela{pv.parcelas > 1 ? 's' : ''} · {br(pv.primeiro_vencimento)}{pv.parcelas > 1 ? ` → ${br(pv.ultimo_vencimento)}` : ''} · {money(pv.valor_parcela)}/parcela
          {loading && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
        </p>
        <p data-testid="pm-previa-resumo">
          <b>{pv.titulos_atualizar}</b> título(s) serão atualizados · <b>{pv.titulos_preservados_pagos}</b> pago(s)/baixado(s) serão preservados
          {pv.titulos_boleto_remessa > 0 && <> · <b>{pv.titulos_boleto_remessa}</b> com boleto/remessa mantêm a data do banco</>}
          {pv.titulos_personalizados_mantidos > 0 && <> · <b>{pv.titulos_personalizados_mantidos}</b> personalizada(s) mantida(s)</>}
        </p>
      </div>
      <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr><th className="px-2 py-1 text-left">Parcela</th><th className="text-left">Status</th><th className="text-left">Vencimento</th><th className="text-right">Valor</th><th className="px-2 text-left">Ação</th></tr>
          </thead>
          <tbody>
            {pv.titulos.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`pm-prev-row-${t.parcela}`}>
                <td className="px-2 py-1">{t.parcela || '—'}</td>
                <td>{t.status}</td>
                <td>{br(t.vencimento_atual)}{t.vencimento_novo && t.vencimento_novo !== t.vencimento_atual ? <b className="text-violet-700"> → {br(t.vencimento_novo)}</b> : ''}</td>
                <td className="text-right">{money(t.valor_atual)}{t.valor_novo !== null && Math.abs(t.valor_novo - t.valor_atual) > 0.004 ? <b className="text-violet-700"> → {money(t.valor_novo)}</b> : ''}</td>
                <td className="px-2">{t.acao === 'preservar' ? <span className="text-emerald-700">preservado</span> : t.acao === 'personalizada' ? <span className="text-amber-700">personalizada (mantida)</span> : t.boleto_remessa ? 'valor (data do banco)' : 'atualizar'}</td>
              </tr>
            ))}
            {pv.titulos.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-slate-400">Nenhum título gerado para este plano.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PlanoAlunoModal({ titleId, open, onOpenChange, onSaved, onEditTitleOnly }) {
  const ctx = useContext(titleId, open);
  const [key, setKey] = useState(null);
  const [f, setF] = useState(null);
  const [pv, setPv] = useState(null);
  const [pvLoading, setPvLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const data = ctx.data;
  const cy = data?.contract?.academic_year;
  const item = useMemo(() => (data?.planos || []).find((p) => p.key === key), [data, key]);
  const orig = useMemo(() => (item ? fromItem(item, cy) : null), [item, cy]);

  useEffect(() => { if (data) setKey((k) => (k && data.planos?.some((p) => p.key === k) ? k : (data.item_key || data.planos?.[0]?.key || null))); }, [data]);
  useEffect(() => { setF(orig); setPv(null); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) { setKey(null); setF(null); } }, [open]);

  useEffect(() => {
    if (!f || !orig || !data) return undefined;
    setPvLoading(true);
    const h = setTimeout(() => {
      api.post(`/student-contracts/contract/${data.contract.contract_id}/plan-item/${encodeURIComponent(key)}/preview`, bodyOf(f, orig))
        .then(({ data: d }) => setPv(d)).catch((e) => { setPv(null); toast.error(errMsg(e, 'Prévia indisponível')); })
        .finally(() => setPvLoading(false));
    }, 350);
    return () => clearTimeout(h);
  }, [f, orig, data, key]);

  const base = Number(orig?.first_year || cy) || new Date().getFullYear();
  const anos = Array.from(new Set([base - 1, base, base + 1, base + 2, Number(f?.first_year) || base])).sort();

  const save = async (sync) => {
    setBusy(true);
    try {
      const { data: r } = await api.patch(`/student-contracts/contract/${data.contract.contract_id}/plan-item/${encodeURIComponent(key)}`,
        { ...bodyOf(f, orig), update_titles: sync });
      toast.success(`Plano atualizado${sync ? ` · ${r.titulos_atualizados || 0} título(s) sincronizado(s)${r.datas_recalculadas ? ` · ${r.datas_recalculadas} vencimento(s) recalculado(s)` : ''}` : ' (títulos não alterados)'}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e, 'Falha ao salvar o plano')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="plano-aluno-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Wallet className="h-4 w-4 text-violet-600" /> Plano financeiro do aluno</DialogTitle>
          <DialogDescription>Edite o plano do contrato; ao sincronizar, só títulos em aberto são recalculados.</DialogDescription>
        </DialogHeader>
        {ctx.loading && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Carregando plano…</p>}
        {ctx.error && (
          <div className="space-y-3" data-testid="pm-sem-plano">
            <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"><AlertTriangle className="mt-0.5 h-4 w-4" /> {ctx.error}</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
              {onEditTitleOnly && <Button variant="outline" onClick={() => { onOpenChange(false); onEditTitleOnly(); }} data-testid="pm-editar-titulo">Editar somente este título</Button>}
            </div>
          </div>
        )}
        {data && f && item && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pm-cabecalho">
              <Kpi label="Aluno" value={data.student_name || '—'} testid="pm-aluno" />
              <Kpi label="Contrato · ano letivo" value={`${data.contract.contract_number || '—'} · ${cy}`} testid="pm-contrato" />
              <Kpi label="Parcelas geradas" value={`${item.gerados}/${item.previstas}`} testid="pm-gerados" />
              <Kpi label="Pagos · abertos" value={`${item.titulos.pagos} · ${item.titulos.pendentes + item.titulos.vencidos}`} tone="text-violet-700" testid="pm-pagos-abertos" />
            </div>
            {data.planos.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="pm-servicos">
                {data.planos.map((p) => (
                  <button key={p.key} type="button" onClick={() => setKey(p.key)} data-testid={`pm-servico-${p.key}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${p.key === key ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-700'}`}>
                    {p.label}{p.key === data.item_key ? ' · deste título' : ''}
                  </button>
                ))}
              </div>
            )}
            <PlanForm f={f} setF={setF} anos={anos} />
            <ParcelasPlanoTable rows={data.titulos?.[key] || []}
              base={`/student-contracts/contract/${data.contract.contract_id}/plan-item/${encodeURIComponent(key)}`}
              onChanged={() => { ctx.reload(); setF((x) => ({ ...x })); onSaved?.(); }} />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Prévia da sincronização com o plano</p>
            <Preview pv={pv} loading={pvLoading} />
            <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="pm-sobrescrever">
              <input type="checkbox" checked={!!f.overwrite_custom} onChange={(e) => setF((x) => ({ ...x, overwrite_custom: e.target.checked }))} data-testid="pm-sobrescrever-check" />
              Sobrescrever parcelas personalizadas
            </label>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="pm-cancelar">Cancelar</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="pm-salvar-plano">Salvar só o plano</Button>
              <Button onClick={() => save(true)} disabled={busy || pvLoading || !pv} className="bg-violet-600 hover:bg-violet-700" data-testid="pm-salvar-sincronizar">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1 h-4 w-4" /> Salvar e sincronizar títulos</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
